import {Direction, Sort} from 'pagination';
import React, {memo, useEffect, useState} from 'react';
import {Referral} from 'referral';
import {BehaviorSubject, combineLatest} from 'rxjs';
import {isValidCombination, QueryableReferralStatus} from '../../hook/useReferralQueryForProvider$';
import {RelayPaginationState} from '../../pagination';
import {ReferralPage} from '../../ReferralPage';
import {useProviderAppReducer} from '../reducer';
import {ReferralFilterState} from "../../filter";


function saveToSessionStorage({referralStatus, sort, pagination, filter}: UrlState) {
    const {by, direction} = sort;

    sessionStorage.setItem('providerReferral:referralStatus', referralStatus);

    sessionStorage.setItem('providerReferral:sortBy', by);
    sessionStorage.setItem('providerReferral:sortDirection', direction);

    sessionStorage.setItem('providerReferral:paginationAfter', pagination.after);

    sessionStorage.setItem('providerReferral:filterStates', JSON.stringify(filter));
}


type UrlState = {
    referralStatus: QueryableReferralStatus;
    sort: Sort<Referral>;
    pagination: RelayPaginationState;
    filter: ReferralFilterState;
};

function initFromSessionStorage(): UrlState {
    const referralStatus = (sessionStorage.getItem('providerReferral:referralStatus') || 'AVAILABLE') as 'AVAILABLE' | 'ACCEPTED';

    const by = (sessionStorage.getItem('providerReferral:sortBy') || 'caseNumber') as keyof Referral;
    const direction = (sessionStorage.getItem('providerReferral:sortDirection') || Direction.ASC) as Direction;
    const sort = {by, direction};

    const after = (sessionStorage.getItem('providerReferral:paginationAfter') || '0');

    const filterFromMemory = sessionStorage.getItem('providerReferral:filterStates');
    const parsedFilter = filterFromMemory && JSON.parse(filterFromMemory)

    let filter: ReferralFilterState = {
        counties: [],
        serviceTypes: [],
        cities: [],
        zipcodes: [],
        scopeOfServices: [],
        serviceLocationTypes: [],
    };

    if (parsedFilter && parsedFilter.counties &&
        parsedFilter.serviceTypes && parsedFilter.cities &&
        parsedFilter.zipcodes && parsedFilter.scopeOfServices &&
        parsedFilter.serviceLocationTypes) {
        filter = parsedFilter;
    }

    if (isValidCombination(sort, referralStatus)) {
        return {referralStatus, sort, pagination: new RelayPaginationState(after), filter};
    } else {
        return {
            referralStatus: 'AVAILABLE',
            sort: {
                by: 'caseNumber',
                direction: Direction.ASC,
            },
            pagination: new RelayPaginationState(after),
            filter,
        };
    }
}

export const SessionStoredReferralPage = memo(function SessionStoreReferralPage() {
    const {sort, referralStatus, pagination, filter} = initFromSessionStorage();

    const [appState, dispatch] = useProviderAppReducer({
        pagination: pagination,
        filter
    });

    const [currentSort$] = useState(() => new BehaviorSubject<Sort<Referral>>(sort));
    const [status$] = useState(() => new BehaviorSubject<QueryableReferralStatus>(referralStatus));
    const [filter$] = useState(() => new BehaviorSubject<ReferralFilterState>(filter));

    useEffect(() => {
        const sub = combineLatest([
            currentSort$,
            status$,
            filter$,
        ]).subscribe(([sortValue, referralStatusValue, filter]) => {
            saveToSessionStorage({
                sort: sortValue,
                referralStatus: referralStatusValue,
                pagination: appState.pagination,
                filter,
            });
        });
        return sub.unsubscribe.bind(sub);
    }, [currentSort$, status$, filter$, appState.pagination]);

    return (
        <ReferralPage appState={appState}
                      dispatch={dispatch}
                      currentSort$={currentSort$}
                      currentStatus$={status$}
                      currentFilter$={filter$}
        />
    );
});
